/**
 * Render the backend's whitelisted RRULE shapes as a short sentence.
 *
 * The rrule grammar is a backend whitelist (RecurrenceService), so this only
 * needs to cover those shapes; anything unrecognised falls back to the raw
 * RRULE text rather than guessing — the label must never claim a cadence the
 * rule does not have.
 */

const WEEKDAY_LABELS: Record<string, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

const FREQ_LABELS: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
  HOURLY: 'Hourly',
};

export function formatRrule(rrule: string): string {
  const parts = new Map<string, string>();
  for (const part of rrule.split(';')) {
    const [key, val] = part.split('=');
    if (key && val) {
      parts.set(key.toUpperCase(), val);
    }
  }
  const freq = parts.get('FREQ');
  const freqLabel = freq ? FREQ_LABELS[freq.toUpperCase()] : undefined;
  if (!freqLabel) {
    return rrule;
  }
  const pieces: string[] = [freqLabel];
  const byday = parts.get('BYDAY');
  if (byday) {
    const days = byday
      .split(',')
      .map(d => WEEKDAY_LABELS[d.toUpperCase()] ?? d)
      .join(', ');
    pieces.push(`on ${days}`);
  }
  const interval = parts.get('INTERVAL');
  if (interval && interval !== '1') {
    pieces.push(`(every ${interval})`);
  }
  const hour = parts.get('BYHOUR');
  if (hour) {
    const minute = (parts.get('BYMINUTE') ?? '0').padStart(2, '0');
    pieces.push(`at ${hour.padStart(2, '0')}:${minute}`);
  }
  return pieces.join(' ');
}
